// [VV5-TEARDOWN] lp-free-shipping flat-shipping A/B test (DEV-9829).
// vv5 lp-free-shipping experiment: variant rule (single source of truth).
//
// Client-safe: this module must NOT import `next/server` (unlike
// `../middleware/freeShipping.ts`), so it can be pulled into client bundles:
// the LP checkout (`@local/offer`) reads the cookie through here to decide
// whether to charge shipping and hide free-shipping messaging.
//
// Reuses the retired `vv5` cookie slot (old `lp-kit` kit / kit-wizard test,
// torn down 2026-06-16) with a fresh 30-day TTL. Old cookies may still hold
// `kit` / `kit-wizard`; `isValidVv5Variant` rejects them so stale browsers are
// re-bucketed into this two-way split.
//
// ─── ASSIGNMENT SCOPE ───────────────────────────────────────────────────────
// Two surfaces share the one slot, flag and seed (DEV-10696 widened the test
// rather than opening a second one, so bucketed LP visitors keep their arm on
// the store and vice versa):
//   - LP:    `/{market}/lp/{FREE_SHIPPING_LP_CHANNELS}/**` in any app. Path
//            scoped by the middleware (`parseFreeShippingPath`).
//   - Store: every `apps/store` page (`AppMiddleware(..., { vv5Store: true })`).
//            Unscoped like vv14: enrolment carries the scope, the store surfaces
//            gate themselves by channel / path (see `isFreeShippingSurface`).
// Exposure fires on the LP highlight mount, on every `(store)` page mount, and
// ahead of the checkout `$pageview` (`checkoutExposure.ts`). PostHog sees ONE
// experiment: split the read by `experiment_lp_free_shipping_surface`.
//
// ─── TEARDOWN CHECKLIST (grep `VV5-TEARDOWN` across the repo) ───────────────
// When this test concludes, remove/revert every touchpoint below. If the flat
// fee WINS, promote it via real entries (one per FREE_SHIPPING_LP_CHANNELS
// slug, plus the store) in `packages/cart/src/cart/SHIPPING_COSTS_CONFIG`
// (protected, ask an owner) instead of the cookie-gated overrides, then delete
// this experiment code. NOTE: the test arm charges GB £2.99, not the £7.95
// storefront rate; see VV5_FLAT_SHIPPING_OVERRIDES. Promote 299 for `gb`, not
// 795.
//
// TEARDOWN ORDER: `customShippingCost` is persisted in the browser cart. Once
// `Vv5StoreShippingController` / the LP hook disappear, test-arm carts keep
// the flat fee until `clearCart` or conversion. Keep a one-release scrub
// (`cart.setShippingCost(undefined)` when the stored cost equals the flat fee,
// never on SUPER_EXPRESS) in the store layout,
// or bump `CART_VERSION` with a cart owner, BEFORE deleting the hook.
//
// DELETE these files (entirely test-only):
//   packages/tracking/src/experiments/freeShipping.ts            (this file)
//   packages/tracking/src/middleware/freeShipping.ts
//   packages/tracking/src/middleware/getVv5.ts
//   packages/ui/src/components/Blocks/Highlight/HighlightNoShipping.tsx
//   packages/ui/src/components/Blocks/Highlight/Vv5ExposureBeacon.tsx
//   packages/ui/src/components/Blocks/ServicePromise/Vv5ServicePromiseHeading.tsx
//   packages/offer/src/modules/common/Header/Vv5Topbar.tsx
//   packages/offer/src/modules/common/Header/Vv5StoreTopbar.tsx
//   packages/offer/src/modules/common/Header/storeTopbarCopy.ts
//   packages/offer/src/modules/store/Cart/Vv5StoreShippingController.tsx
//   packages/payment/src/hooks/useVv5FlatShipping.ts
//   packages/payment/src/hooks/__tests__/useVv5FlatShipping.test.tsx
//   packages/tracking/src/__tests__/freeShippingMiddleware.test.ts
//   packages/tracking/src/__tests__/freeShippingExperiment.test.ts
//   packages/payment/src/helpers/__tests__/flatShipping.test.ts
//   packages/tracking/src/experiments/checkoutExposure.ts    (vv5 is its only
//     consumer since the vv10 teardown, so the whole file goes)
//   packages/tracking/src/__tests__/checkoutExposure.test.ts
//   packages/ui/src/components/Blocks/ServicePromise/__tests__/Vv5ServicePromiseHeading.test.tsx
//   packages/offer/src/__tests__/Vv5Topbar.test.tsx
//   docs/experimentation-ab-test-free-shipping.md
//
// REVERT the marked `[VV5-TEARDOWN]` blocks in these files:
//   packages/tracking/src/middleware/index.ts        (wiring: header + cookie + `vv5Store` option)
//   packages/tracking/src/middleware/url/const.ts     (vv5 param comment)
//   packages/tracking/src/trackActions.tsx            (split_test vv5 membership)
//   apps/store/src/proxy.ts                           (`vv5Store: true`)
//   apps/store/src/app/[market]/(store)/layout.tsx    (Vv5ExposureBeacon + Vv5StoreShippingController mounts)
//   apps/store/src/app/[market]/(store)/(home)/page.tsx (Vv5StoreTopbar in the header data)
//   packages/offer/src/modules/common/Header/StoreHeader.tsx (Vv5StoreTopbar in the header data)
//   packages/payment/src/helpers/shippingRates.ts     (getFlatShippingCost +
//     VV5_FLAT_SHIPPING_OVERRIDES + `flatShipping` params)
//   packages/payment/src/modules/stripe/express.tsx   (flatShipping gate + args)
//   packages/payment/src/modules/paypal/usePayPalExpressPayment.ts
//   packages/payment/src/components/ShippingSelector.tsx
//   packages/payment/src/modules/stripe/useStripeCheckout.ts (order metadata tag)
//   packages/payment/src/modules/paypal/shared.ts            (order metadata tag)
//   packages/payment/src/helpers/getFreeOrderSessionBody.ts  (order metadata tag)
//   packages/offer/src/builder.tsx                    (dispatch + beacon + ContentIconsRow gate + ServicePromise channel)
//   apps/store/src/app/[market]/(multi)/lp/[channel]/(home)/layout.tsx (passes freeShippingChannels)
//   packages/offer/src/modules/lp/LpHomeHeader.tsx   (mounts Vv5Topbar; keep `topbar: formattedMessage` and drop the prop)
//   packages/offer/src/modules/common/Header/types.ts (HeaderProps.data topbar widened to ReactNode)
//   packages/offer/src/modules/lp/Checkout/CheckoutOfferBuilder.tsx (fee hook + messaging + checkout topbar copy)
//   packages/offer/src/modules/lp/Checkout/components/QuantitySelector/QuantitySelector.tsx (hasFreeShipping)
//   packages/ui/src/components/Blocks/Highlight/index.tsx           (hideShippingInformation)
//   packages/ui/src/components/Blocks/ContentIconsRow/index.tsx     (hideFreeShipping)
//   packages/ui/src/components/Blocks/ServicePromise/index.tsx      (channel prop + testTitle)
//   packages/ui/src/components/Blocks/ServicePromise/ServicePromiseIcon.tsx (testTitle prop)
//   packages/offer/src/modules/common/StaticPages/CookiePolicy/en.tsx (vv5 cookie row)
//   packages/offer/src/modules/common/StaticPages/CookiePolicy/de.tsx (vv5 cookie row)
//   packages/devtools/src/panel/components/ABTestingTab.tsx (vv5 EXPERIMENTS entry)
//   apps/store/src/tests/policy-pages.spec.ts         (vv5 cookie-policy assertion)
//   docs/experimentation-how-to-add-an-ab-test.md     (§0.3 slot registry row)
//
// KEEP (permanent refactors that landed with the test):
//   packages/offer/src/modules/common/Header/formatTopbar.ts
//
// EXTERNAL: archive the `lp-free-shipping` PostHog feature flag.
// ───────────────────────────────────────────────────────────────────────────

import { toPlainText } from "@local/utils/src/toPlainText"

/** Cookie / query-override slot name for the vv5 experiment. */
export const VV5_COOKIE = "vv5"

/**
 * PostHog feature-flag key for this experiment (single source of truth).
 * The `control` / `test` cookie buckets map 1:1 to this flag's variant keys.
 * Exposure is fired client-side on the LP highlight mount and on every store
 * page mount, see
 * `packages/ui/src/components/Blocks/Highlight/Vv5ExposureBeacon.tsx`, and,
 * ordered ahead of the checkout `$pageview`, by
 * `packages/tracking/src/experiments/checkoutExposure.ts` (DEV-9883).
 */
export const VV5_FEATURE_FLAG = "lp-free-shipping"

/**
 * The landing-page channels this experiment runs on. Their ainchor sub-pages
 * (`/lp/{channel}/{ainchor}`) are in scope too: they render the same blocks
 * and funnel into the same checkout.
 *
 * `sunbeater-hoodie` since DEV-9829; `rancher-shirt` and
 * `backpack-hoodie-women` added by DEV-10024, `leggings` by DEV-10360,
 * `cargo-pants` by DEV-10665; each increments the same test rather than
 * spinning up a second one, so already-bucketed visitors keep their arm on the
 * new pages.
 */
export const FREE_SHIPPING_LP_CHANNELS = [
  "sunbeater-hoodie",
  "rancher-shirt",
  "backpack-hoodie-women",
  "leggings",
  "cargo-pants",
] as const

/**
 * Whether a channel slug is an enrolled LANDING PAGE. Exact membership, so
 * the unenrolled `backpack-hoodie` never matches `backpack-hoodie-women`.
 *
 * Deliberately LP-only even though the store is enrolled too (DEV-10696):
 * `packages/offer/src/builder.tsx` gates the server-side `getVv5()` read on
 * it, and that read would turn the statically prerendered store home dynamic.
 * Use `isFreeShippingEnrolledChannel` for "is this channel in the test at all".
 */
export const isFreeShippingLpChannel = (channel?: string | null): boolean =>
  !!channel &&
  (FREE_SHIPPING_LP_CHANNELS as readonly string[]).includes(channel)

/**
 * Backend channel slug of the main storefront (baerskintactical.com), enrolled
 * store-wide by DEV-10696.
 */
export const FREE_SHIPPING_STORE_CHANNEL = "store"

/** Whether a channel slug is the storefront. Exact match. */
export const isFreeShippingStoreChannel = (channel?: string | null): boolean =>
  channel === FREE_SHIPPING_STORE_CHANNEL

/**
 * Whether a channel slug is enrolled in the experiment on ANY surface (an LP
 * or the storefront). Shared blocks (`ServicePromise`) gate on this so a
 * store-issued cookie never swaps copy on an unenrolled LP whose checkout
 * still ships free.
 */
export const isFreeShippingEnrolledChannel = (
  channel?: string | null
): boolean =>
  isFreeShippingLpChannel(channel) || isFreeShippingStoreChannel(channel)

/**
 * Valid variant values for the vv5 lp-free-shipping experiment. Even split
 * (50 / 50):
 *   - control → free shipping everywhere, all free-shipping messaging shown
 *   - test    → a flat shipping fee charged on every order (the store's local
 *               rate, except where VV5_FLAT_SHIPPING_OVERRIDES lowers it (GB
 *               is £2.99), all free-shipping messaging removed
 */
export type FreeShippingVariant = "control" | "test"
const VALID_VARIANTS: FreeShippingVariant[] = ["control", "test"]

/**
 * Whether a raw cookie / override string is a valid current variant. Rejects
 * the stale `kit` / `kit-wizard` values left by the old lp-kit test so those
 * browsers fall through to a fresh assignment.
 */
export const isValidVv5Variant = (
  value: string | null | undefined
): value is FreeShippingVariant =>
  value != null && VALID_VARIANTS.includes(value as FreeShippingVariant)

/**
 * Whether the visitor should be charged the flat shipping fee (and have
 * free-shipping messaging removed). Only the `test` arm charges; a missing /
 * invalid / control value keeps free shipping (safe default: matches the
 * current experience).
 */
export const shouldChargeShipping = (vv5?: string): boolean => vv5 === "test"

/**
 * Per-channel promo wording for the test-arm banner (DEV-10431). The test arm
 * replaces the CMS banner, so a page whose promotion is not a percentage needs
 * its own line here: `leggings` runs a buy-2-get-1, and the percentage fallback
 * advertised a discount that page does not have. `cargo-pants` (DEV-10665)
 * sells at 52%, not the 60% default; the LP topbar has no computed discount,
 * so the entry pins the percentage on both surfaces. A channel with no entry
 * keeps the percentage line, so the pages already in the test are untouched.
 */
const TEST_BANNER_OFFERS: Readonly<Record<string, string>> = {
  "leggings": "BUY 2 LEGGINGS GET 1 FREE",
  "cargo-pants": "52% OFF",
}

/**
 * The promo half of the test-arm banner: the channel's own wording when it has
 * one, else the discount percentage.
 */
const testBannerOffer = (channel?: string | null, discount = 60): string =>
  (channel ? TEST_BANNER_OFFERS[channel] : undefined) ?? `${discount}% OFF`

/**
 * Test-arm promo-banner copy: the free-shipping wording removed. Used for BOTH
 * the LP header topbar and the checkout marquee banner (single source so the two
 * surfaces never drift). US keeps the delivery + returns line minus "& FREE";
 * other markets use the returns-only line with a siren. The control arm keeps
 * the CMS banner untouched, so this is only rendered when `vv5 === "test"`.
 *
 * On the store this is only the FALLBACK: the store strips the clause out of
 * the weekly CMS template instead (`stripFreeShippingClause`) and lands here
 * only when nothing readable is left.
 */
export const freeShippingTestBanner = ({
  market,
  channel,
  discount = 60,
}: {
  market?: string | null
  channel?: string | null
  discount?: number
}): string => {
  const offer = testBannerOffer(channel, discount)

  return market?.toLowerCase() === "us"
    ? `${offer} + FAST USA DELIVERY + 60-DAY RETURNS!`
    : `🚨 ${offer} + 60-DAY RETURNS!`
}

/**
 * Optional threshold tail of a free-shipping clause, matched on the RAW CMS
 * template (placeholders still in place), so one pattern covers every market
 * and currency: `OVER %%CURRENCY%%%%FREE_SHIPPING_THRESHOLD%%`, DE `AB …`.
 */
const THRESHOLD_TAIL = String.raw`(?:\s*(?:over|above|from|on\s+orders\s+over|ab|über)\s*%%CURRENCY%%\s*%%FREE_SHIPPING_THRESHOLD%%)?`

/**
 * The connective that glues the clause onto the promo before it (`+`, `&`,
 * `,`, `|`, `·`, `•`, "and", "und"). Folded into the match so `66% OFF + FREE
 * DELIVERY …` leaves `66% OFF`, not `66% OFF +`.
 */
const CONNECTIVE = String.raw`(?:\s*(?:[+&,|·•]|\band\b|\bund\b))?`

/**
 * Every shape the weekly CRM banner has used for the free-shipping promise.
 * `%%ACTION%%` is the CMS placeholder that `formatTopbarTemplate` turns into
 * "SHIPPING", so it must be matched before substitution.
 */
const FREE_SHIPPING_CLAUSE_PATTERNS: readonly RegExp[] = [
  new RegExp(
    String.raw`${CONNECTIVE}\s*(?:fast\s*(?:&|and)\s*)?free\s+(?:%%ACTION%%|shipping|delivery)${THRESHOLD_TAIL}`,
    "gi"
  ),
  new RegExp(
    String.raw`${CONNECTIVE}\s*(?:gratis|kostenlose[rn]?)\s+(?:versand|lieferung)${THRESHOLD_TAIL}`,
    "gi"
  ),
  new RegExp(
    String.raw`${CONNECTIVE}\s*versandkostenfrei${THRESHOLD_TAIL}`,
    "gi"
  ),
]

/** A connective left dangling at either end once the clause is gone. */
const DANGLING_CONNECTIVE_RE = /^\s*[+&,|·•]\s*|\s*[+&,|·•]\s*$/g

/**
 * Remove the free-shipping clause from a RAW CMS topbar template (before
 * `%%CURRENCY%%` / `%%FREE_SHIPPING_THRESHOLD%%` substitution) for the store's
 * test arm, keeping the rest of the week's promo intact:
 *
 *   `LABOR DAY SALE! 66% OFF + FREE DELIVERY OVER %%CURRENCY%%%%FREE_SHIPPING_THRESHOLD%%`
 *   → `LABOR DAY SALE! 66% OFF`
 *
 * Returns the input by reference when no clause matches, so callers can detect
 * "nothing to strip" with `===`. `null` / `undefined` pass straight through.
 *
 * @param template - Raw CMS topbar copy, HTML allowed
 * @returns The template with every free-shipping clause removed and tidied
 */
export const stripFreeShippingClause = <T extends string | null | undefined>(
  template: T
): T => {
  if (typeof template !== "string") {
    return template
  }

  let stripped: string = template
  for (const pattern of FREE_SHIPPING_CLAUSE_PATTERNS) {
    stripped = stripped.replace(pattern, "")
  }

  if (stripped === template) {
    return template
  }

  return stripped
    .replace(DANGLING_CONNECTIVE_RE, "")
    .replace(/\s{2,}/g, " ")
    .trim() as T
}

/**
 * Whether a (possibly HTML) string still carries a letter or a digit once the
 * markup is gone. Guards the topbar fallback: a template that was ONLY the
 * free-shipping clause strips down to `""` or a lone `<strong></strong>`.
 *
 * @param text - Copy to inspect, HTML allowed
 * @returns True when at least one letter or digit remains
 */
export const hasMeaningfulCopy = (text: string | null | undefined): boolean =>
  !!text && /[\p{L}\p{N}]/u.test(toPlainText(text))

/**
 * Whether a USP tile heading is the free-shipping promise ("FREE SHIPPING",
 * "Free Delivery", DE "Gratis Versand" / "Kostenlose Lieferung" /
 * "Versandkostenfrei"), HTML allowed. "Free Exchanges" and "Fast Shipping"
 * are not.
 *
 * @param heading - Tile heading, HTML allowed
 * @returns True when the heading promises free shipping
 */
export const isFreeShippingHeading = (
  heading: string | null | undefined
): boolean =>
  !!heading &&
  /\bfree\s+(?:shipping|delivery)\b|\b(?:gratis|kostenlose[rn]?)\s+(?:versand|lieferung)\b|versandkostenfrei/i.test(
    toPlainText(heading)
  )

/**
 * Whether a pathname is one of the enrolled free-shipping LPs or anything
 * beneath it (`/{market}/lp/{channel}`, its ainchor sub-pages, its checkout),
 * but NOT a different LP that merely starts with an enrolled slug (e.g.
 * `…/sunbeater-hoodie-sale`), because the slug must terminate at a `/` or the
 * end of the path. That terminator is also what keeps the unenrolled
 * `backpack-hoodie` out while `backpack-hoodie-women` is in.
 *
 * Anchored at the market segment, mirroring `parseFreeShippingPath` in the
 * middleware: a persisted `vv5` cookie must not leak the fee / order tag onto
 * a look-alike route, and an unanchored match would accept nested shapes the
 * middleware rejects (`/us/other/lp/rancher-shirt`) plus the market-less legacy
 * form, which `(legacy)/lp/[slug]` redirects away before anything renders.
 */
const FREE_SHIPPING_LP_PATH_RE = new RegExp(
  `^/[^/]+/lp/(${FREE_SHIPPING_LP_CHANNELS.join("|")})(?:/|$)`
)
export const isFreeShippingLpPath = (pathname?: string | null): boolean =>
  !!pathname && FREE_SHIPPING_LP_PATH_RE.test(pathname)

/**
 * The enrolled channel a pathname points at, or null when the path is not an
 * enrolled LP. Same rule as `isFreeShippingLpPath`, which is its boolean
 * projection, so the two can never disagree.
 *
 * The checkout needs the slug, not just the boolean: it enrols a visitor by the
 * backend channel slug OR by the pathname, and only the pathname carries the
 * enrolled slug when the two diverge. Picking the test-arm promo copy off a
 * divergent backend slug would drift the marquee from the LP topbar.
 */
export const freeShippingLpChannel = (
  pathname?: string | null
): string | null =>
  (pathname ? FREE_SHIPPING_LP_PATH_RE.exec(pathname)?.[1] : null) ?? null

/**
 * Whether a pathname is the storefront checkout, `/{market}/checkout`, or
 * anything beneath it. The market segment may be a language variant
 * (`de-en`, DEV-9850), so it is `[^/]+`, mirroring FREE_SHIPPING_LP_PATH_RE.
 * Never matches `/{market}/lp/{slug}/checkout` (the second segment must be
 * `checkout`) nor the market-less legacy `/checkout` (a `route.ts` redirect).
 */
const STORE_CHECKOUT_PATH_RE = /^\/[^/]+\/checkout(?:\/|$)/
export const isStoreCheckoutPath = (pathname?: string | null): boolean =>
  !!pathname && STORE_CHECKOUT_PATH_RE.test(pathname)

/**
 * Whether a client-side flow runs on an enrolled surface: an enrolled LP or
 * the storefront, detected by the cart channel slug OR the URL pathname, so
 * express can't silently fall back to free if the `channel` prop ever
 * diverges from the URL.
 */
export const isFreeShippingSurface = ({
  channel,
  pathname,
}: {
  channel?: string | null
  pathname?: string | null
}): boolean =>
  isFreeShippingEnrolledChannel(channel) ||
  isFreeShippingLpPath(pathname) ||
  isStoreCheckoutPath(pathname)

/**
 * Whether the flat shipping fee should be charged for a client-side flow
 * (main checkout + express/wallet). True only on the `test` arm AND on an
 * enrolled surface (see `isFreeShippingSurface`).
 */
export const isFreeShippingTestActive = ({
  channel,
  pathname,
  vv5,
}: {
  channel?: string
  pathname?: string | null
  vv5?: string
}): boolean =>
  shouldChargeShipping(vv5) && isFreeShippingSurface({ channel, pathname })

/** Which enrolled surface an order was placed on. */
export type FreeShippingSurface = "lp" | "store"

/**
 * Order-payload tag for server-side per-arm revenue analysis (survives
 * ad-blockers, unlike client PostHog). Returns the variant plus the surface
 * only for orders placed on an enrolled LP checkout or the store checkout with
 * a valid variant, else `{}` so unrelated orders aren't tagged. Pure (no
 * cookie/window access) so it stays edge-safe: callers pass the pathname +
 * cookie value.
 *
 * The key `experiment_lp_free_shipping` predates the store surface and is kept
 * so the existing datalake / haus readers keep working;
 * `experiment_lp_free_shipping_surface` splits LP from store revenue without a
 * join on the channel id (which is `channelId` in free orders but `channel_id`
 * in Stripe / PayPal ones).
 */
export const freeShippingOrderTag = (
  pathname: string | null | undefined,
  vv5: string | undefined
):
  | {
      experiment_lp_free_shipping: FreeShippingVariant
      experiment_lp_free_shipping_surface: FreeShippingSurface
    }
  | Record<string, never> => {
  if (!isValidVv5Variant(vv5)) {
    return {}
  }
  if (isFreeShippingLpPath(pathname)) {
    return {
      experiment_lp_free_shipping: vv5,
      experiment_lp_free_shipping_surface: "lp",
    }
  }
  if (isStoreCheckoutPath(pathname)) {
    return {
      experiment_lp_free_shipping: vv5,
      experiment_lp_free_shipping_surface: "store",
    }
  }
  return {}
}
